/**
 * OMON-149: load the client-context block rendered above the SOMA plan
 * view — age, address summary, planned carers, today's working carers.
 *
 * Data flow:
 *
 *   _episodeofcare/{eocDocId}
 *     subject[0].reference
 *       → either _patient/{id} (OneTouch-synced pilots)
 *       → or     _person/{id}  (test/dev pilots — no _patient doc exists)
 *
 *   For OneTouch-synced pilots, the _patient.link._person.reference
 *   points to the canonical _person doc with birthDate + address[].
 *   For test pilots, those fields live directly on the role-_person doc
 *   the EoC's subject points at — there is no _patient, and there are
 *   no encounters to query.
 *
 *   Encounters are queried by `subject.reference == <DocumentReference>`
 *   (Firestore can't index inside `episodeOfCare.0.reference` — the
 *   array-of-maps gotcha — so we use the top-level subject ref instead;
 *   see ticket OMON-149 + `~/brain/feedback_firestore_array_of_maps_query_gotcha`).
 *   IMPORTANT: must pass the DocumentReference object, not `.path` —
 *   the latter is a JS-side property, NOT a queryable subfield.
 *   We then client-side filter to `meta.source == "onetouch-sync"` so
 *   the ONE-004 anchor-encounter docs (different shape, no participant
 *   data) are ignored.
 *
 *   Practitioner names are batch-fetched with the same chunked
 *   `where(documentId(), "in", chunk)` pattern that `loadPatientPlan`
 *   uses for `_goal` (max 30 IDs per `in` query).
 *
 * Pure-helper exports (computeAge, summariseAddress, extractPlannedCarers,
 * extractTodaysCarers) keep the derivation logic unit-testable
 * independently of Firestore.
 */

import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  query,
  where,
  DocumentReference,
  type DocumentData,
  type DocumentSnapshot,
} from "firebase/firestore";
import { db } from "../firebase";
import { canReadAccessRequests } from "../admin/claims";
import { state } from "../state";
import type {
  PatientInfo,
  ClientContextData,
  ClientAddress,
  CarerSummary,
} from "../types";
import {
  applyPendingRequests, avatarStoragePath, projectFamilyContact, sortFamilyContacts,
  type PendingRequest,
} from "../clients/family-contacts";

const EMPTY_CONTEXT: ClientContextData = {
  plannedCarers: [],
  todaysCarers: [],
  familyContacts: [],
};

/**
 * `_person` docs serialise array fields as either `address: [...]` or
 * `address: { "0": {...} }` depending on which writer authored them
 * (agent.fhir vs agent.onetouch). This unwraps either shape and
 * returns the first element, or undefined.
 */
export function firstElementOfMaybeArray<T>(value: unknown): T | undefined {
  if (Array.isArray(value) && value.length > 0) return value[0] as T;
  if (value && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    if ("0" in obj) return obj["0"] as T;
  }
  return undefined;
}

/** Normalise `_person.address[0]` (or `_person.address["0"]`) into ClientAddress. */
function normaliseAddress(raw: unknown): ClientAddress | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as Record<string, unknown>;
  const lineRaw = r.line;
  let line: string[] | undefined;
  if (Array.isArray(lineRaw)) {
    line = lineRaw.filter((x): x is string => typeof x === "string");
  } else if (lineRaw && typeof lineRaw === "object") {
    // Object-as-array shape: {"0": "15 Star Street"}
    line = Object.values(lineRaw).filter((x): x is string => typeof x === "string");
  }
  return {
    line: line && line.length > 0 ? line : undefined,
    district: typeof r.district === "string" && r.district !== "unused" ? r.district : undefined,
    city: typeof r.city === "string" && r.city !== "unused" ? r.city : undefined,
    postalCode: typeof r.postalCode === "string" && r.postalCode !== "unused" ? r.postalCode : undefined,
  };
}

/**
 * Compute integer age in years between birthDate and `now`. Returns
 * undefined if birthDate is unparseable or yields a negative age.
 *
 * birthDate strings observed in production take two shapes:
 *   - "1974-12-25" (date-only, from OneTouch sync)
 *   - "1974-12-25T00:00:00+00:00" (datetime, from agent.fhir seed)
 * `new Date()` handles both.
 */
export function computeAge(birthDateIso: string, now: Date = new Date()): number | undefined {
  const bd = new Date(birthDateIso);
  if (isNaN(bd.getTime())) return undefined;
  let age = now.getFullYear() - bd.getFullYear();
  const monthsDelta = now.getMonth() - bd.getMonth();
  if (monthsDelta < 0 || (monthsDelta === 0 && now.getDate() < bd.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : undefined;
}

/**
 * Pick the most useful single string to display for an address.
 * Preference order: district → first line → city → undefined.
 *
 * District is preferred because Ed's mockup shows "Ealing Broadway" —
 * a recognisable area, more useful at-a-glance than a street number.
 */
export function summariseAddress(addr: ClientAddress | undefined): string | undefined {
  if (!addr) return undefined;
  if (addr.district) return addr.district;
  if (addr.line && addr.line.length > 0) return addr.line[0];
  if (addr.city) return addr.city;
  return undefined;
}

/**
 * Raw shape for a `_encounter` doc as it lands in this loader. We only
 * read the fields we care about; everything else is ignored.
 */
export interface EncounterRaw {
  status?: string;
  meta?: { source?: string };
  participant?: Array<{ actor?: { reference?: DocumentReference } }>;
  period?: { start?: string; end?: string };
}

/**
 * True for `_encounter` docs that are a carer's shift: the OneTouch visit sync,
 * or the demo rota (agent.fhir/scripts/demo_rota.py), which has no OneTouch.
 */
function isOneTouchVisit(e: EncounterRaw): boolean {
  return e.meta?.source === "onetouch-sync" || e.meta?.source === "demo-rota";
}

/** Pull the encounter's primary-performer practitioner doc ID, if any. */
function primaryPerformerId(e: EncounterRaw): string | undefined {
  const ref = e.participant?.[0]?.actor?.reference;
  return ref?.id;
}

/**
 * The first day the carer panels look at: today, as the UTC calendar date
 * (the sync writes Z-suffixed `period.start` strings, and both extractors
 * below compare `period.start.slice(0, 10)` against this).
 *
 * ☠️ OMON-351: this is ALSO the lower bound of the `_encounter` query
 * (`period.start >= encounterWindowStart(now)`). For any string, `s >= D`
 * holds exactly when `s.slice(0, 10) >= D` (D is a 10-char date prefix), so
 * the query returns precisely the encounters the extractors keep — nothing
 * they would have shown is dropped. Change one and you must change the other;
 * client-context.test.ts pins the equivalence.
 */
export function encounterWindowStart(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/**
 * Distinct carers visiting the client across the encounter window
 * (today onwards, sorted alphabetically). Filters to OneTouch visit
 * encounters; ignores ONE-004 anchor encounters.
 */
export function extractPlannedCarers(
  encounters: EncounterRaw[],
  practitionerNames: Map<string, string>,
  now: Date = new Date(),
): CarerSummary[] {
  const todayDate = encounterWindowStart(now);
  const seen = new Map<string, CarerSummary>();
  for (const e of encounters) {
    if (!isOneTouchVisit(e)) continue;
    if (!e.period?.start) continue;
    if (e.period.start.slice(0, 10) < todayDate) continue;
    const id = primaryPerformerId(e);
    if (!id || seen.has(id)) continue;
    seen.set(id, { practitionerDocId: id, name: practitionerNames.get(id) || id });
  }
  return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Carers whose shift starts today (in local time) and whose encounter
 * status is `planned` or `in-progress` (i.e. not cancelled or already
 * finished). Sorted alphabetically.
 */
export function extractTodaysCarers(
  encounters: EncounterRaw[],
  practitionerNames: Map<string, string>,
  now: Date = new Date(),
): CarerSummary[] {
  const todayDate = encounterWindowStart(now);
  const seen = new Map<string, CarerSummary>();
  for (const e of encounters) {
    if (!isOneTouchVisit(e)) continue;
    if (e.status !== "planned" && e.status !== "in-progress") continue;
    if (!e.period?.start) continue;
    if (e.period.start.slice(0, 10) !== todayDate) continue;
    const id = primaryPerformerId(e);
    if (!id || seen.has(id)) continue;
    seen.set(id, { practitionerDocId: id, name: practitionerNames.get(id) || id });
  }
  return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * First usable `telecom` value of a system, tolerating the object-as-array
 * shape and agent.fhir's "unused" sentinel (same rule the admin card uses).
 */
function firstTelecom(raw: unknown, system: string): string | undefined {
  const entries = Array.isArray(raw)
    ? raw
    : raw && typeof raw === "object" ? Object.values(raw as Record<string, unknown>) : [];
  for (const entry of entries) {
    if (!entry || typeof entry !== "object") continue;
    const t = entry as { system?: unknown; value?: unknown };
    if (t.system !== system) continue;
    const value = typeof t.value === "string" ? t.value.trim() : "";
    if (value && value !== "unused") return value;
  }
  return undefined;
}

/**
 * The references the client-context block starts from, read off the EoC
 * with no further reads (OMON-351: the reads that follow from them all start
 * at once, instead of queueing behind the subject → person walk).
 */
interface EpisodeRefs {
  subjectRef: DocumentReference | null;
  /** The subject when it is a `_patient` (OneTouch pilots); null for test pilots. */
  patientRef: DocumentReference | null;
  careManagerRef: DocumentReference | null;
  managingOrgRef: DocumentReference | null;
}

function episodeRefs(eocData: Record<string, unknown> | undefined): EpisodeRefs {
  if (!eocData) return { subjectRef: null, patientRef: null, careManagerRef: null, managingOrgRef: null };
  const subjectEntry = firstElementOfMaybeArray<{ reference?: DocumentReference }>(eocData?.subject);
  const subjectRef = subjectEntry?.reference;

  // careManager extraction is independent of subject resolution — even
  // if subject is missing/malformed we still surface the care manager
  // if present.
  const cmEntry = firstElementOfMaybeArray<{ reference?: DocumentReference }>(eocData?.careManager);
  const careManagerRef = (cmEntry?.reference instanceof DocumentReference) ? cmEntry.reference : null;

  // managingOrganization is the sync-stamped, clone-authoritative client
  // area (ONE-017) — the practice line prefers it over carer-majority.
  //
  // SHAPE TRAP (fixed 2026-08-06): unlike `subject` and `careManager`, which
  // are arrays, `managingOrganization` is a PLAIN object on the EoC
  // ({___use, active, reference}). firstElementOfMaybeArray only unwraps
  // arrays and {"0": …} maps, so it returned undefined here and the practice
  // line silently fell through to carer-majority forever. Accept all three
  // shapes.
  const moRaw = eocData?.managingOrganization;
  const moEntry =
    firstElementOfMaybeArray<{ reference?: DocumentReference }>(moRaw)
    ?? (moRaw as { reference?: DocumentReference } | undefined);
  const managingOrgRef = (moEntry?.reference instanceof DocumentReference) ? moEntry.reference : null;

  if (!(subjectRef instanceof DocumentReference)) {
    return { subjectRef: null, patientRef: null, careManagerRef, managingOrgRef };
  }
  const patientRef = subjectRef.path.startsWith("_patient/") ? subjectRef : null;
  return { subjectRef, patientRef, careManagerRef, managingOrgRef };
}

/**
 * Resolve `EoC.subject[0].reference` → `_person` facts. Handles both shapes
 * the production data carries:
 *
 *   - real OneTouch pilots: subject → _patient → link._person → _person
 *   - test/dev pilots:      subject → _person (terminal; no _patient)
 *
 * OMON-351: also returns the `_patient` doc's data (when the subject is one),
 * so the MONO-106 avatar reads it from here instead of fetching it again.
 */
async function resolveSubjectPerson(subjectRef: DocumentReference): Promise<{
  birthDate?: string;
  address?: ClientAddress;
  phone?: string;
  patientData?: Record<string, unknown>;
}> {
  let personRef: DocumentReference | null = null;
  let patientData: Record<string, unknown> | undefined;

  if (subjectRef.path.startsWith("_patient/")) {
    const patientSnap = await getDoc(subjectRef);
    patientData = patientSnap.exists() ? (patientSnap.data() as Record<string, unknown>) : undefined;
    const linkRef = (patientData?.link as { _person?: { reference?: unknown } } | undefined)?._person?.reference;
    if (linkRef instanceof DocumentReference) {
      personRef = linkRef;
    }
  } else if (subjectRef.path.startsWith("_person/")) {
    personRef = subjectRef;
  }

  if (!personRef) return { patientData };

  const personSnap = await getDoc(personRef);
  if (!personSnap.exists()) return { patientData };
  const personData = personSnap.data();
  const birthDate = typeof personData?.birthDate === "string" ? personData.birthDate : undefined;
  const address = normaliseAddress(firstElementOfMaybeArray(personData?.address));
  // ADR-0046 §6: the person card shows a client's phone beside their address.
  // Read here, off the `_person` this traversal already has — no extra read,
  // and no second copy of the episode→patient→person walk.
  const phone = firstTelecom(personData?.telecom, "phone");
  return { birthDate, address, phone, patientData };
}

/**
 * Extract a human-display name from a doc's `name` field.
 *
 * Handles both array (`name: [{given:[...], family:...}]`) and
 * object-as-array (`name: {"0": {given: {"0": ...}, family: ...}}`)
 * shapes — OMON-156 will eventually normalise the writers, but until
 * then readers tolerate both. Filters out placeholder values like
 * `"unused"` (agent.fhir test-data sentinel).
 *
 * Returns undefined if the field is missing/empty (so callers can
 * decide whether to fall back to another source — e.g. follow a
 * `link._person` reference for OneTouch-synced _practitioner docs
 * that carry the name only on the linked canonical person).
 */
function extractDisplayName(data: Record<string, unknown> | undefined): string | undefined {
  if (!data) return undefined;
  const nameEntry = firstElementOfMaybeArray<Record<string, unknown>>(data.name);
  if (!nameEntry) return undefined;
  const givenRaw = nameEntry.given;
  let given = "";
  if (Array.isArray(givenRaw)) {
    given = givenRaw.filter((x): x is string => typeof x === "string" && x !== "unused").join(" ");
  } else if (givenRaw && typeof givenRaw === "object") {
    given = Object.values(givenRaw).filter((x): x is string => typeof x === "string" && x !== "unused").join(" ");
  } else if (typeof givenRaw === "string" && givenRaw !== "unused") {
    given = givenRaw;
  }
  const family = (typeof nameEntry.family === "string" && nameEntry.family !== "unused")
    ? nameEntry.family
    : "";
  const display = [given, family].filter(Boolean).join(" ").trim();
  return display || undefined;
}

/**
 * Extract the OneTouch "area" label from a `_practitioner` doc's
 * extension array. agent.onetouch writes `extension[]` with one entry
 * whose `url` is `https://sweettreeconnect.com/fhir/extension/onetouch-organisation`
 * and whose inner `extension[]` carries `area`, `workgroup`, `branch`,
 * `location` entries. Prefer `area` (more specific — e.g. "Live In -
 * Dementia - Ashleigh") and fall back to `workgroup` (broader — e.g.
 * "Live In - Dementia") if area is missing.
 */
function extractPractitionerArea(data: Record<string, unknown> | undefined): string | undefined {
  if (!data) return undefined;
  const extensions = data.extension;
  if (!Array.isArray(extensions)) return undefined;
  const orgExt = extensions.find((e: unknown) => {
    return typeof e === "object" && e !== null
      && (e as Record<string, unknown>).url === "https://sweettreeconnect.com/fhir/extension/onetouch-organisation";
  }) as Record<string, unknown> | undefined;
  const inner = orgExt?.extension;
  if (!Array.isArray(inner)) return undefined;
  const findByUrl = (url: string): string | undefined => {
    const hit = inner.find((e: unknown) =>
      typeof e === "object" && e !== null && (e as Record<string, unknown>).url === url
    ) as Record<string, unknown> | undefined;
    const v = hit?.valueString;
    return typeof v === "string" ? v : undefined;
  };
  return findByUrl("area") ?? findByUrl("workgroup");
}

/**
 * Resolve a set of `_practitioner` doc IDs to {name, area} summaries.
 *
 * Why this is more than one query: OneTouch-synced `_practitioner` docs
 * have **no name field directly**. The name lives on the linked
 * `_person` doc (via `link._person.reference`). Manual-seed pilots have
 * the name on a ROLE _person doc directly (no _practitioner doc at all
 * in some cases). So this helper:
 *
 *   1. Batch-fetches `_practitioner` docs by ID
 *   2. Tries to extract name + area directly from each
 *   3. For practitioners with no direct name, collects the linked
 *      `_person` ref
 *   4. Batch-fetches those `_person` docs to resolve names
 *   5. Returns a unified map per practitioner ID
 *
 * Falls back to the doc ID as display name if name resolution fails
 * — so the UI never shows blank, only either a real name or the ID.
 *
 * The "area" comes from the `_practitioner` doc itself (its OneTouch
 * organisation extension), not the linked person.
 */
export interface PractitionerInfo {
  name: string;
  area?: string;
}

export async function batchFetchPractitionerInfo(ids: string[]): Promise<Map<string, PractitionerInfo>> {
  const out = new Map<string, PractitionerInfo>();
  if (ids.length === 0) return out;

  const ID_CHUNK_SIZE = 30;
  const chunkIds = (arr: string[]): string[][] => {
    const chunks: string[][] = [];
    for (let i = 0; i < arr.length; i += ID_CHUNK_SIZE) chunks.push(arr.slice(i, i + ID_CHUNK_SIZE));
    return chunks;
  };

  // Step 1: fetch _practitioner docs
  const pracSnaps = await Promise.all(chunkIds(ids).map((c) =>
    getDocs(query(collection(db, "_practitioner"), where(documentId(), "in", c))),
  ));

  // Track which practitioners still need name resolution via link._person
  const personRefByPracId = new Map<string, DocumentReference>();
  pracSnaps.forEach((snap) => {
    snap.forEach((d) => {
      const data = d.data();
      const directName = extractDisplayName(data);
      const area = extractPractitionerArea(data);
      if (directName) {
        out.set(d.id, { name: directName, area });
      } else {
        // Queue the linked _person for fetch; track area now
        const linkRef = data?.link?._person?.reference;
        if (linkRef instanceof DocumentReference) {
          personRefByPracId.set(d.id, linkRef);
        }
        // Still record area + placeholder name; may overwrite name later
        out.set(d.id, { name: d.id, area });
      }
    });
  });

  // Step 2: batch-fetch the linked _person docs for any practitioner
  // that didn't have a direct name (typical for OneTouch-synced data)
  const personIds = Array.from(new Set(
    Array.from(personRefByPracId.values()).map((r) => r.id),
  ));
  if (personIds.length > 0) {
    const personSnaps = await Promise.all(chunkIds(personIds).map((c) =>
      getDocs(query(collection(db, "_person"), where(documentId(), "in", c))),
    ));
    const nameByPersonId = new Map<string, string>();
    personSnaps.forEach((snap) => {
      snap.forEach((d) => {
        const display = extractDisplayName(d.data());
        if (display) nameByPersonId.set(d.id, display);
      });
    });
    // Backfill names into out map
    for (const [pracId, personRef] of personRefByPracId.entries()) {
      const display = nameByPersonId.get(personRef.id);
      if (display) {
        const existing = out.get(pracId);
        out.set(pracId, { name: display, area: existing?.area });
      }
    }
  }

  return out;
}

/**
 * Compute the "practice" for a client — the most common `area` value
 * across the carers assigned to them. Tie-breaks alphabetically by
 * area name so the result is deterministic.
 *
 * Returns undefined if no carers have an area, or if the carer set
 * is empty.
 */
function deriveClientPractice(carers: CarerSummary[], info: Map<string, PractitionerInfo>): string | undefined {
  const counts = new Map<string, number>();
  for (const c of carers) {
    const area = info.get(c.practitionerDocId)?.area;
    if (!area) continue;
    counts.set(area, (counts.get(area) ?? 0) + 1);
  }
  if (counts.size === 0) return undefined;
  const sorted = Array.from(counts.entries()).sort((a, b) => {
    if (b[1] !== a[1]) return b[1] - a[1];
    return a[0].localeCompare(b[0]);
  });
  return sorted[0][0];
}

/**
 * Resolve a careManager DocumentReference (which may point at `_person`
 * for manually-seeded pilots) to a display name. Falls back through
 * the linked _person if the directly-referenced doc has no name field.
 */
async function resolveCareManagerName(ref: DocumentReference): Promise<string | undefined> {
  try {
    const snap = await getDoc(ref);
    if (!snap.exists()) return undefined;
    const data = snap.data();
    const direct = extractDisplayName(data);
    if (direct) return direct;
    // Try following link._person — typical when the ref points at a
    // _practitioner doc that's a OneTouch-synced role doc with name
    // on the canonical person only.
    const linkRef = data?.link?._person?.reference;
    if (linkRef instanceof DocumentReference) {
      const personSnap = await getDoc(linkRef);
      if (personSnap.exists()) {
        return extractDisplayName(personSnap.data());
      }
    }
    return undefined;
  } catch (err) {
    console.warn("resolveCareManagerName failed:", err);
    return undefined;
  }
}

/**
 * Planned + today's carers for a client, from their upcoming `_encounter`s.
 *
 * 2026-05-28: encounters are queried by Patient ref, honouring ADR-0014 D8.
 *
 * ADR-0014 D8 specifies that OneTouch shift encounters are written
 * with `Encounter.subject.reference → _patient/{id}` (FHIR-canonical).
 * 8,725 of 8,760 OneTouch-tagged encounters in prod follow that
 * shape; querying by EoC ref returns 0 of them. The OMON-160-era
 * comment that previously lived here (claiming "all 243 encounters
 * got migrated to subject=EoC") only described the bridge-encounter
 * migration — it did NOT touch the OneTouch shift encounters,
 * which is what this loader actually needs.
 *
 * For test pilots where EoC.subject points directly at a _person/
 * doc (no separate _patient), there is no patientRef and this is not
 * called: those EoCs don't typically have OneTouch shift data, so we
 * treat them as having zero encounters rather than fall back to an
 * eocRef query that would only return bridge encounters (which are
 * filtered out by isOneTouchVisit anyway).
 *
 * OMON-149 query shape note still applies: pass the
 * DocumentReference object directly, NOT `.path` — the path
 * property is JS-side on the SDK object, not a queryable subfield.
 *
 * OMON-351 (2026-09-24): bounded to `period.start >= today`. It used to read
 * the client's ALL-TIME encounters (~57 per client and ~300 more a day across
 * prod), then fetch a `_practitioner` + `_person` for every past carer, only
 * for both extractors to discard everything before today. The bound is the
 * extractors' own cut (see `encounterWindowStart`), so the panels are
 * unchanged; practitioner ids now come only from the encounters that can
 * appear. Same shape as clients/rota-week.ts, so the existing composite index
 * (subject.reference ASC + period.start ASC) serves it — no new index.
 *
 * Strict-rules implications: once strict rules return, the
 * Encounter `allow read` predicate currently traverses
 * `get(data.subject.reference).careTeam[0]`. With subject pointing
 * at a Patient, that traversal will fail (Patients have no
 * careTeam). The rule needs to traverse via
 * `data.episodeOfCare[0].reference → _episodeofcare → careTeam`
 * instead. Tracked as a followup; not blocking today because the
 * current prod rules are permissive. (The date bound adds no field the
 * rules read, so it changes nothing here.)
 */
async function loadUpcomingCarers(patientRef: DocumentReference, now: Date): Promise<{
  plannedCarers: CarerSummary[];
  todaysCarers: CarerSummary[];
  info: Map<string, PractitionerInfo>;
}> {
  const encounters: EncounterRaw[] = [];
  const encSnap = await getDocs(query(
    collection(db, "_encounter"),
    where("subject.reference", "==", patientRef),
    where("period.start", ">=", encounterWindowStart(now)),
  ));
  encSnap.forEach((d) => encounters.push(d.data() as EncounterRaw));

  const ids = new Set<string>();
  for (const e of encounters) {
    if (!isOneTouchVisit(e)) continue;
    const id = primaryPerformerId(e);
    if (id) ids.add(id);
  }
  const info = await batchFetchPractitionerInfo(Array.from(ids));
  // Compose a name-only map for the carer-summary extractors (they
  // don't need area, only name); deriveClientPractice uses the full
  // info map directly.
  const names = new Map<string, string>();
  for (const [pracId, pi] of info.entries()) {
    names.set(pracId, pi.name);
  }
  return {
    plannedCarers: extractPlannedCarers(encounters, names, now),
    todaysCarers: extractTodaysCarers(encounters, names, now),
    info,
  };
}

/**
 * Family & contacts — the ONE-015 `_relatedPerson` mirrors for this
 * client (OneTouch-canonical identity; read-only here by design — fixes
 * happen in OneTouch and sync down). `patient` on the mirror is a direct
 * DocumentReference to the _patient doc; same query-shape rule as the
 * encounter query (pass the ref object, never `.path`). Bind state needs
 * one ROLE `_person` read per contact (circles are small — single digits).
 */
async function loadFamilyContacts(patientRef: DocumentReference): Promise<ClientContextData["familyContacts"]> {
  const rpSnap = await getDocs(query(
    collection(db, "_relatedPerson"),
    where("patient", "==", patientRef),
  ));
  const projections = await Promise.all(rpSnap.docs.map(async (d) => {
    const data = d.data() as Record<string, unknown>;
    if (data.active === false) return null;
    // ROLE person: `link._person.reference` → ROOT; the ROLE doc is
    // the sibling `_person` linking to the same ROOT with
    // resourceType RelatedPerson (the doc the bind stamps).
    let roleUid: string | null = null;
    const link = (data.link as { _person?: { reference?: unknown } } | undefined);
    const rootRef = link?._person?.reference;
    if (rootRef instanceof DocumentReference) {
      const roleSnap = await getDocs(query(
        collection(db, "_person"),
        where("link._person.reference", "==", rootRef),
      ));
      for (const rs of roleSnap.docs) {
        const rd = rs.data() as Record<string, unknown>;
        if (rd.resourceType !== "RelatedPerson") continue;
        const uid = ((rd.identifier as { googleIdentityPlatform?: { value?: unknown } } | undefined)
          ?.googleIdentityPlatform?.value);
        roleUid = typeof uid === "string" ? uid : null;
        break;
      }
    }
    return projectFamilyContact(data, roleUid, d.id);
  }));
  const contacts = projections.filter((p): p is NonNullable<typeof p> => p !== null);
  return sortFamilyContacts(applyPendingRequests(contacts, await loadPendingRequests(contacts)));
}

/**
 * Pending app-access requests naming any of these contacts (2026-10-24), so
 * the card can show "asked to join". One query (array-contains-any takes up
 * to 30 values — a client's circle is single digits); status is filtered
 * here, so no composite index. Best-effort: a read failure (e.g. a ruleset
 * that doesn't let this role read requests) shows the contacts without it.
 */
async function loadPendingRequests(contacts: { docId?: string }[]): Promise<PendingRequest[]> {
  // Office roles only — the strict rules refuse everyone else, and a refused
  // best-effort read is indistinguishable from "no requests" (admin/claims.ts
  // canReadAccessRequests has the story).
  if (!canReadAccessRequests(state.authClaims)) return [];
  const ids = contacts.map((c) => c.docId).filter((i): i is string => !!i).slice(0, 30);
  if (ids.length === 0) return [];
  try {
    const snap = await getDocs(query(
      collection(db, "_relatedPersonRequest"),
      where("matchedRelatedPersonDocIds", "array-contains-any", ids),
    ));
    return snap.docs
      .map((d) => ({ id: d.id, data: d.data() as Record<string, unknown> }))
      .filter(({ data }) => data.status === "pending")
      .map(({ id, data }) => {
        const at = data.requestedAt as { toDate?: () => Date } | undefined;
        return {
          id,
          matchedRelatedPersonDocIds: Array.isArray(data.matchedRelatedPersonDocIds)
            ? (data.matchedRelatedPersonDocIds as unknown[]).filter((i): i is string => typeof i === "string")
            : [],
          requestedAt: typeof at?.toDate === "function" ? at.toDate().toISOString() : undefined,
        };
      });
  } catch (err) {
    console.warn("family contacts: pending requests unreadable — shown without them", err);
    return [];
  }
}

/**
 * Load the full client-context block for a patient's plan view.
 *
 * Resilient by design: any sub-step failure (missing person, missing
 * patient link, query error) degrades to an empty context rather than
 * throwing — the header should always render *something*, even if it's
 * just the EoC ID.
 *
 * OMON-351: one EoC read (shared with the plan loader when the caller passes
 * `opts.episode`), then EVERY branch starts at once — subject → person,
 * upcoming encounters → carers, family contacts, care manager, practice org.
 * It used to walk EoC → patient → person → patient (again, for the avatar) →
 * encounters → practitioners → persons, one wait after another.
 */
export async function loadClientContext(
  patient: PatientInfo,
  opts: { episode?: Promise<DocumentSnapshot<DocumentData>> } = {},
): Promise<ClientContextData> {
  try {
    // One clock for the query bound and both extractors, so a load that
    // straddles midnight UTC can't bound by one day and filter by the next.
    const now = new Date();
    const eocSnap = await (opts.episode ?? getDoc(doc(db, "_episodeofcare", patient.docId)));
    const { subjectRef, patientRef, careManagerRef, managingOrgRef } =
      episodeRefs(eocSnap.exists() ? eocSnap.data() : undefined);

    // Care manager resolves independently of patient/encounter data.
    const careManagerPromise = careManagerRef
      ? resolveCareManagerName(careManagerRef)
      : Promise.resolve(undefined);

    // Practice: the EoC's managingOrganization (sync-stamped from the
    // clone-authoritative client area, ONE-017) is the client's OWN
    // team. Carer-majority (deriveClientPractice) stays as the fallback
    // for EoCs with no org stamp — it describes where the assigned
    // carers are rostered, which can lag or differ from the client's
    // area (the 2026-08-06 Spinal case: client moved to the new Spinal
    // team, carers still rostered in BI-East).
    const orgNamePromise: Promise<string | undefined> = managingOrgRef
      ? getDoc(managingOrgRef)
          .then((s) => (s.exists() && typeof s.data()?.name === "string" ? s.data()!.name as string : undefined))
          .catch(() => undefined)
      : Promise.resolve(undefined);

    const [subject, careManager, orgName, familyContacts, carers] = await Promise.all([
      subjectRef ? resolveSubjectPerson(subjectRef) : Promise.resolve({} as Awaited<ReturnType<typeof resolveSubjectPerson>>),
      careManagerPromise,
      orgNamePromise,
      patientRef ? loadFamilyContacts(patientRef) : Promise.resolve([]),
      patientRef
        ? loadUpcomingCarers(patientRef, now)
        : Promise.resolve({ plannedCarers: [], todaysCarers: [], info: new Map<string, PractitionerInfo>() }),
    ]);

    const { birthDate, address, phone, patientData } = subject;
    const result: ClientContextData = {
      birthDate,
      age: birthDate ? computeAge(birthDate) : undefined,
      address,
      addressSummary: summariseAddress(address),
      phone,
      plannedCarers: carers.plannedCarers,
      todaysCarers: carers.todaysCarers,
      familyContacts: familyContacts ?? [],
    };
    if (patientRef) {
      result.patientDocId = patientRef.id;
      // MONO-106: client avatar (photo[0] Attachment, written by agent.media),
      // off the `_patient` doc the subject walk already read.
      if (patientData) result.clientAvatarPath = avatarStoragePath(patientData);
    }
    result.practice = orgName ?? deriveClientPractice(carers.plannedCarers, carers.info);
    result.careManager = careManager;
    return result;
  } catch (err) {
    console.warn(`loadClientContext: ${patient.docId}`, err);
    return EMPTY_CONTEXT;
  }
}
